import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API, useAuth } from '../contexts/AuthContext';

function toDateInput(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toISOString().slice(0, 10);
}

function formatMin(v) {
  if (v == null || Number.isNaN(v)) return '—';
  if (v < 60) return `${v} min`;
  if (v < 1440) {
    const h = Math.floor(v / 60);
    const m = v % 60;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  }
  const d = Math.floor(v / 1440);
  const h = Math.floor((v % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
}

function corSla(p) {
  if (p == null) return '#5E7A82';
  if (p >= 90) return '#4CB553';
  if (p >= 70) return '#fe8d25';
  return '#b43737';
}

// Mini sparkline para tendencia diaria
function Sparkline({ dados }) {
  if (!dados.length) return null;
  const w = 320;
  const h = 60;
  const max = Math.max(1, ...dados.map((d) => Math.max(d.criados, d.resolvidos)));
  const stepX = dados.length > 1 ? w / (dados.length - 1) : 0;
  const px = (i) => i * stepX;
  const py = (v) => h - (v / max) * h;
  const path = (key) => dados.map((d, i) => `${i === 0 ? 'M' : 'L'} ${px(i).toFixed(1)} ${py(d[key]).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="auto" style={{ marginTop: 8 }}>
      <path d={path('criados')} fill="none" stroke="#2A8A9C" strokeWidth="1.8" />
      <path d={path('resolvidos')} fill="none" stroke="#4CB553" strokeWidth="1.8" />
    </svg>
  );
}

function MultiSelect({ placeholder, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length ? `${value.length} selecionado(s)` : placeholder;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', minWidth: 140 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', padding: '3px 8px',
          background: 'var(--portal-surface)', border: '1px solid var(--portal-border)',
          borderRadius: 4, cursor: 'pointer', textAlign: 'left', fontSize: '0.8rem',
          color: value.length ? 'var(--portal-text)' : 'var(--portal-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ flexShrink: 0, fontSize: '0.65rem' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, minWidth: '100%',
          maxHeight: 180, overflowY: 'auto', background: 'var(--portal-surface)',
          border: '1px solid var(--portal-border)', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', marginTop: 2,
        }}>
          {options.length === 0
            ? <div style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Sem opcoes</div>
            : options.map((o) => (
              <label key={o.value} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                cursor: 'pointer', fontSize: '0.82rem', color: 'var(--portal-text)',
                background: value.includes(o.value) ? 'rgba(42,138,156,0.08)' : 'transparent',
              }}>
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} style={{ flexShrink: 0 }} />
                {o.label}
              </label>
            ))}
        </div>
      )}
    </div>
  );
}

function saudacaoPorHorario() {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

export default function Dashboard() {
  const { user, temPermissao } = useAuth();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [metricas, setMetricas] = useState(null);
  const [canalId, setCanalId] = useState('');
  const [atendentes, setAtendentes] = useState([]);
  const [canaisDisp, setCanaisDisp] = useState([]);
  const [atendentesDisp, setAtendentesDisp] = useState([]);

  const podeMetricas = temPermissao('ti:atendimentos', 'admin:atendimento', 'ti:dashboard');

  const carregar = (canal, ats) => {
    const fim = new Date();
    const ini = new Date();
    ini.setDate(fim.getDate() - 30);
    const params = { inicio: toDateInput(ini), fim: toDateInput(fim) };
    if (canal) params.canal_id = canal;
    if (ats && ats.length) params.atendentes = ats.join(',');
    setLoading(true);
    setErro('');
    axios
      .get(`${API}/metricas/atendimento`, { params })
      .then((res) => setMetricas(res.data?.data || null))
      .catch(() => setErro('Nao foi possivel carregar os indicadores.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!podeMetricas) {
      setLoading(false);
      return;
    }
    carregar('', []);
    Promise.allSettled([
      axios.get(`${API}/atendimento/canais`),
      axios.get(`${API}/metricas/atendentes`),
    ]).then(([rcan, rat]) => {
      if (rcan.status === 'fulfilled') setCanaisDisp(rcan.value.data?.data || []);
      if (rat.status === 'fulfilled') setAtendentesDisp((rat.value.data?.data || []).map((a) => ({ value: a, label: a })));
    });
  }, [podeMetricas]);

  if (!podeMetricas) {
    return (
      <div className="content-shell">
        <section className="page-header">
          <div>
            <span className="section-header__eyebrow">Portal TI</span>
            <h1>Dashboard</h1>
          </div>
        </section>
        <div className="empty-state compact">
          <strong>Sem indicadores liberados</strong>
          <span>Solicite ao administrador o perfil com permissoes de atendimento.</span>
        </div>
      </div>
    );
  }

  const primeiroNome = (user?.nome || user?.usuario || '').split(' ')[0];
  const subInfos = [user?.perfil_nome, user?.filial && `Filial ${user.filial}`, user?.setor]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Portal TI</span>
          <h1 style={{ fontSize: '1.4rem', lineHeight: 1.2 }}>
            {saudacaoPorHorario()}{primeiroNome ? `, ${primeiroNome}` : ''}!
          </h1>
          <p style={{ fontSize: '0.85rem', marginTop: 4 }}>
            {subInfos || 'Visao consolidada dos modulos da TI.'}
          </p>
        </div>
      </section>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      {/* ========== Bloco: Atendimento ========== */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <header style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-headset" style={{ fontSize: '1.1rem', color: '#2A8A9C' }} />
            <h2 style={{
              fontSize: '1.05rem',
              margin: 0,
              color: 'var(--portal-text)',
              fontFamily: 'inherit',
            }}>
              Atendimento
            </h2>
            <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
              · ultimos 30 dias
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {canaisDisp.length > 0 && (
              <select
                value={canalId}
                onChange={(e) => { setCanalId(e.target.value); carregar(e.target.value, atendentes); }}
                style={{ fontSize: '0.8rem', padding: '3px 6px', borderRadius: 4, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)' }}
              >
                <option value="">Todos os canais</option>
                {canaisDisp.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            )}
            {atendentesDisp.length > 0 && (
              <MultiSelect
                placeholder="Todos atendentes"
                options={atendentesDisp}
                value={atendentes}
                onChange={(v) => setAtendentes(v)}
              />
            )}
            {(canalId || atendentes.length > 0) && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => carregar(canalId, atendentes)}
                style={{ background: '#2A8A9C', color: '#fff', border: 'none', fontSize: '0.78rem' }}
                disabled={loading}
              >
                <i className="bi bi-funnel me-1" />Aplicar
              </button>
            )}
            <Link to="/ti/atendimentos/indicadores" className="btn btn-sm btn-outline-info">
              <i className="bi bi-graph-up me-1" /> Indicadores completos
            </Link>
          </div>
        </header>

        {loading ? (
          <div className="content-card content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando indicadores...</p>
          </div>
        ) : metricas ? (
          <>
            <div className="row g-3" style={{ margin: 0 }}>
              <div className="col-6 col-lg-3">
                <article className="content-card" style={{ padding: 14, borderLeft: '4px solid #2A8A9C' }}>
                  <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>Criados</div>
                  <strong style={{ fontSize: '1.5rem', color: 'var(--portal-text)' }}>{metricas.totais.criados}</strong>
                </article>
              </div>
              <div className="col-6 col-lg-3">
                <article className="content-card" style={{ padding: 14, borderLeft: '4px solid #4CB553' }}>
                  <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>Resolvidos</div>
                  <strong style={{ fontSize: '1.5rem', color: 'var(--portal-text)' }}>{metricas.totais.resolvidos}</strong>
                </article>
              </div>
              <div className="col-6 col-lg-3">
                <article className="content-card" style={{ padding: 14, borderLeft: '4px solid #fe8d25' }}>
                  <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>Backlog (agora)</div>
                  <strong style={{ fontSize: '1.5rem', color: 'var(--portal-text)' }}>{metricas.backlog.total || 0}</strong>
                  <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)' }}>
                    {metricas.backlog.urgentes || 0} urgente(s)
                  </div>
                </article>
              </div>
              <div className="col-6 col-lg-3">
                <article className="content-card" style={{ padding: 14, borderLeft: `4px solid ${corSla(metricas.sla.ttfr_perc)}` }}>
                  <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>% SLA TTFR</div>
                  <strong style={{ fontSize: '1.5rem', color: 'var(--portal-text)' }}>
                    {metricas.sla.ttfr_perc != null ? `${metricas.sla.ttfr_perc}%` : '—'}
                  </strong>
                  <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)' }}>
                    {metricas.sla.ttfr_dentro}/{metricas.sla.ttfr_avaliados} dentro
                  </div>
                </article>
              </div>
            </div>

            <div className="row g-3" style={{ margin: 0 }}>
              <div className="col-12 col-lg-4">
                <article className="content-card" style={{ padding: 14 }}>
                  <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>TTFR medio</div>
                  <strong style={{ fontSize: '1.2rem', color: 'var(--portal-text)' }}>
                    {formatMin(metricas.tempos.ttfr_min_medio)}
                  </strong>
                  <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)' }}>
                    TTR medio: {formatMin(metricas.tempos.ttr_min_medio)}
                  </div>
                </article>
              </div>

              <div className="col-12 col-lg-8">
                <article className="content-card" style={{ padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                      Criados x Resolvidos por dia
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--portal-muted)' }}>
                      <span style={{ color: '#2A8A9C' }}>━ Criados</span>
                      {' '}
                      <span style={{ color: '#4CB553' }}>━ Resolvidos</span>
                    </span>
                  </div>
                  <Sparkline dados={metricas.tendencia} />
                </article>
              </div>
            </div>

            {metricas.distribuicoes.por_categoria.length > 0 && (
              <article className="content-card" style={{ padding: 14 }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 8 }}>
                  Top categorias
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {metricas.distribuicoes.por_categoria.slice(0, 5).map((c) => {
                    const total = metricas.totais.criados || 1;
                    const perc = Math.round((c.total / total) * 100);
                    return (
                      <div key={c.categoria}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                          <span>{c.nome || c.categoria}</span>
                          <span style={{ color: 'var(--portal-muted)' }}>{c.total}</span>
                        </div>
                        <div style={{ height: 6, background: 'var(--portal-surface-soft)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${perc}%`, height: '100%', background: '#2A8A9C' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            )}
          </>
        ) : null}
      </section>

      {/* Demais blocos (Equipamentos, Chamados, Insumos, ...) entram aqui
          conforme novos modulos do dashboard forem implementados. Cada um
          deve seguir o mesmo padrao: <header> com icone+titulo+link e
          conteudo abaixo. */}
    </div>
  );
}
